import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { ArrowRight, Download, FileBarChart2 } from "lucide-react";

export default function Reports() {
  const { t, lang } = useI18n();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/me").then((r) => setReports(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="label-divider mb-3">{lang === "fr" ? "Reporting mensuel" : "Monthly reports"}</div>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-slate-900" data-testid="reports-title">
          {lang === "fr" ? "Reportings mensuels" : "Monthly reports"}
        </h1>
        <Button asChild variant="outline" className="rounded-sm" data-testid="reports-back">
          <Link to="/dashboard"><ArrowRight className="h-4 w-4 mr-2 rotate-180" /> {t.nav.dashboard}</Link>
        </Button>
      </div>
      <p className="text-sm text-slate-500 mt-2 max-w-2xl">
        {lang === "fr"
          ? "Synthèse opérationnelle mensuelle par SPV : occupation, uptime, énergie, revenus locatifs et capitalisation prévisionnelle. Pour mémoire, le versement effectif des coupons aux investisseurs est semestriel."
          : "Monthly operational summary per SPV: occupancy, uptime, energy, rental revenues and forecast capitalisation. Note: actual coupon payouts to investors are semi-annual."}
      </p>

      {loading ? (
        <div className="mt-10 text-sm uppercase tracking-[0.2em] text-slate-500">Chargement…</div>
      ) : reports.length === 0 ? (
        <div className="mt-10 bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-600" data-testid="reports-empty">
          {lang === "fr" ? "Aucun reporting disponible pour le moment." : "No reports available yet."}
        </div>
      ) : (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {reports.map((r) => (
            <div key={r.report_id} className="bg-white border border-slate-200 rounded-sm p-6 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`report-card-${r.report_id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <span className="inline-block text-[10px] uppercase tracking-[0.2em] bg-slate-900 text-white font-bold px-2 py-1 rounded-sm">{r.year_month}</span>
                  <h3 className="font-display text-lg font-semibold text-slate-900 mt-3">{r.spv_name}</h3>
                </div>
                <FileBarChart2 className="h-5 w-5 text-emerald-700" strokeWidth={1.5} />
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3 text-xs border-t border-slate-100 pt-4">
                <Stat k={lang === "fr" ? "Occupation" : "Occupancy"} v={`${r.kpis.occupancy_pct}%`} />
                <Stat k="Uptime" v={`${r.kpis.uptime_pct}%`} />
                <Stat k={lang === "fr" ? "Revenus" : "Revenues"} v={`${Math.round(r.kpis.monthly_revenue_eur).toLocaleString()}€`} />
                <Stat k={lang === "fr" ? "Capitalisé" : "Capitalised"} v={`${Math.round(r.kpis.monthly_distribution_eur).toLocaleString()}€`} />
              </div>
              <div className="mt-6 flex gap-2">
                <Button asChild size="sm" className="rounded-sm bg-slate-900 hover:bg-slate-800 h-8 text-xs" data-testid={`report-view-${r.report_id}`}>
                  <Link to={`/dashboard/reports/${r.report_id}`}>{lang === "fr" ? "Consulter" : "View"}</Link>
                </Button>
                <a
                  href={`${API}/reports/${r.report_id}/pdf`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center text-xs h-8 px-3 border border-slate-300 rounded-sm hover:bg-slate-50 text-slate-700"
                  data-testid={`report-pdf-${r.report_id}`}
                >
                  <Download className="h-3 w-3 mr-1.5" /> PDF
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ k, v }) {
  return (
    <div>
      <div className="text-slate-500 text-[10px] uppercase tracking-[0.15em]">{k}</div>
      <div className="font-mono font-semibold text-slate-900 mt-1">{v}</div>
    </div>
  );
}
